/**
 * Inline options surface for the Schema Compare Setup dialog.
 *
 * Combines the depth preset selector with an engine-aware object-type
 * checkbox row. Long-tail tuning lives in a separate Advanced sub-dialog
 * (case/whitespace + engine-filtered properties-to-compare checklist) to
 * keep vertical space tight in Setup.
 *
 * R3-2 — one control here is NOT about the comparison: "Wrap in a single
 * transaction" describes the SCRIPT the comparison will later generate. It sits
 * in this block because this block is the profile a user exports, loads and
 * saves as a per-engine default, and script atomicity is part of that decision;
 * as a toolbar checkbox it could be shared with nobody. It is also the one
 * control gated on the DESTINATION engine rather than the source — see the
 * `destinationEngine` prop.
 */

import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { EyeOff, Info, Save, ShieldCheck, SlidersHorizontal, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { getEngineDisplayInfo } from '@/core/engines';
import { useAppStore } from '../../../../store/appStore';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SchemaCompareOptions, SchemaFilter } from '../../core/types/workspaceTypes';
import {
  applyDepthPreset,
  applyEngineDefaults,
  deepDiffersFromStandard,
  getStandardIgnoredProperties,
  inferDepth,
  ENGINE_OBJECT_TYPES,
  ENGINE_PROPERTY_ALLOWLIST,
  quickDepthSkippedList,
  type CompareDepth,
} from '../utils/compareDepth';
import { engineWrapsInTransaction } from '../utils/wrapCapableEngines';
import { engineSupportsIgnoreOwner } from '../utils/ownerIgnoreCapableEngines';
import { wrapInTransactionTooltip } from '../utils/wrapInTransactionTooltip';
import { SchemaCompareAdvancedDialog } from './SchemaCompareAdvancedDialog';
import { IgnoredPropertiesSummaryButton } from './IgnoredPropertiesSummaryButton';
import { SchemaCompareOptionsFileButtons } from './SchemaCompareOptionsFileButtons';

type Props = {
  /**
   * What the dialog wants DISPLAYED — the raw options with the Standard-depth
   * fallback already applied (`applyStandardDepthFallback`). Never spread this
   * into a state write: writes go through `onChange`'s updater form so the
   * derived depth keys can't leak into the dialog's raw `options` state
   * (W5.2 fix round 1, F-2).
   */
  options: SchemaCompareOptions;
  /**
   * The dialog's `setOptions`. Updater form is REQUIRED for anything that
   * isn't an explicit depth choice — see the handlers below.
   */
  onChange: Dispatch<SetStateAction<SchemaCompareOptions>>;
  /** Source engine drives which object types & properties are surfaced. */
  engine: DatabaseEngineId | undefined;
  /**
   * R3-2 — the engine the generated sync script will RUN against, which is a
   * different question from `engine` above and must not be folded into it.
   * `engine` is the SOURCE (it decides which object types exist and which
   * properties are comparable); the transaction wrap is a property of the
   * script, so it belongs to the TARGET. On a cross-engine comparison the two
   * differ, and using the source here would offer a T-SQL-flavoured wrap on a
   * PostgreSQL target — the exact defect `wrapInTransactionTooltip` exists to
   * prevent, one level up.
   *
   * `undefined` (target connection not resolved yet) hides the control, per
   * `engineWrapsInTransaction`'s documented contract: a caller that does not
   * know its engine must not be offered, or silently given, the wrap.
   */
  destinationEngine?: DatabaseEngineId;
  /**
   * W5.1b — the Setup dialog's unified schema filter. Lives outside
   * `options` (see `SchemaCompareSetupDialog`'s own `schemaFilter` state),
   * so it's threaded in/out separately for the Export/Load options file
   * buttons, which need to read AND restore it alongside `options`.
   */
  schemaFilter: SchemaFilter | undefined;
  onSchemaFilterChange: Dispatch<SetStateAction<SchemaFilter | undefined>>;
  disabled?: boolean;
  /**
   * W3.5/F07 seam 2 — the dialog is comparing exactly one already-picked
   * object (`compareWholeDatabase: false`), so the "Object types" checkbox
   * row has nothing left to choose: whichever type the object picker
   * selected is the only one this compare will ever touch, regardless of
   * what these checkboxes say (`schemaFetcher.ts` forces it). A rendered
   * control with no effect is a defect in this codebase — hide the row
   * rather than leave it checkable-but-inert. Compare depth and Format SQL
   * stay: both still genuinely affect a single-object compare.
   */
  singleObjectMode?: boolean;
};

// W5.2 (D3, F03) — verified against the actual fetch code
// (`electron/metadataService.ts:245-248` for the Quick/light-mode skip,
// `electron/schema-compare/schemaFetcher.ts:180+246` for triggers) rather than
// restated from the old (false) copy. Quick is the only preset that changes
// what gets FETCHED. Standard and Deep fetch identically — full per-table
// metadata including triggers, table options, and partitions — and differ
// only in which volatile storage properties are pre-ignored from the diff.
//
// W5.2 fix round 1 (F-1, M-1, M-2): engine-derived, because the previous
// engine-agnostic wording was false on two of five engines. "A few volatile
// storage properties" is ZERO pre-ignored properties for PostgreSQL and SQLite
// (where Standard and Deep are literally the same comparison) and exactly ONE
// for MySQL. The property names come straight from `STANDARD_IGNORED_BY_ENGINE`,
// so the card can never claim a suppression the compare doesn't perform.
// Quick's skip list also now matches the docs bullet in
// `website/src/docs/schema-compare.njk` verbatim, partitions included.
//
// W5.2 fix round 2 (P-2): scoped to what JAM does, never to what the ENGINE
// has. Claiming an engine "has no volatile storage properties" reads as simply
// wrong to anyone who knows that engine; "Jam pre-ignores nothing on X — none
// of the properties it compares there are volatile-storage ones" is true and
// says the same operative thing.
//
// W5.2 fix round 3: round 2 used PostgreSQL as the example of that, on the
// strength of `ENGINE_PROPERTY_ALLOWLIST.pgsql` not listing `fillFactor` — but
// that turned out to be a BUG in the allow-list, not a fact about what Jam
// compares: `comparePgsql` diffs fillFactor, the fetcher parses it out of
// `reloptions_text`, and the DDL emitter writes it. Both are fixed in
// `compareDepth.ts`, so PostgreSQL now pre-ignores `fillFactor` at Standard
// like SQL Server does and has a real, enabled Deep preset. **SQLite is now the
// only engine this empty-list branch describes** — `withoutRowid`/`strict` are
// structural declarations, not storage tuning.
//
// W5.2 fix round 5 (D1): the "volatile storage drift" sentence below is only
// true because `identitySeed`/`identityIncrement` are no longer in ANY engine's
// Standard list. Measured on live Oracle 23.26.1.0.0 and Azure SQL Edge
// 15.0.2000.1574: what Jam reads is the DECLARED seed
// (`ALL_TAB_IDENTITY_COLS.IDENTITY_OPTIONS` / `sys.identity_columns.seed_value`),
// which does not move when rows are inserted — only `LAST_NUMBER` / `last_value`
// does, and Jam never reads either. See the `STANDARD_IGNORED_BY_ENGINE` comment
// in `compareDepth.ts`. Re-adding them makes this sentence false again AND makes
// a dev-vs-prod ID-range difference read as "Identical".
//
// U6 — the class list is no longer written out here. It now comes from
// `QUICK_DEPTH_SKIPPED_CLASSES` in `compareDepth.ts`, the same constant the
// results-side disclosure renders from, so the setup card and the results
// banner cannot drift into naming different sets of skipped classes.
const QUICK_DESCRIPTION =
  `Columns only — skips ${quickDepthSkippedList()}. Fastest on large schemas.`;

/** P-2 phrasing, shared by the depth card, the tooltip, and the unavailable note. */
function nothingPreIgnoredPhrase(engineName: string): string {
  return `Jam pre-ignores nothing on ${engineName} — none of the properties it compares there are volatile-storage ones`;
}

/**
 * R3-5c (showcase review round 3) — Standard's card ran to four lines, the
 * longest of the three, and unbalanced the row.
 *
 * The cut is exactly ONE claim, and it is not dropped, it is de-duplicated:
 * the old copy opened by enumerating what "full metadata" covers
 * ("constraints, indexes, triggers, table options, partition definitions") —
 * the SAME five classes the Quick card immediately to its left already
 * enumerates as what Quick skips, from the same `quickDepthSkippedList()`
 * constant. Saying it relative to Quick ("everything Quick skips") is shorter,
 * reads as a progression across the three cards, and is strictly MORE accurate
 * per engine: on SQLite Quick only skips triggers (`compareDepth.ts` §2.3), so
 * the hardcoded five-class list overstated what Standard adds there while the
 * relative phrasing cannot.
 *
 * Everything else survives verbatim, deliberately:
 *   - "Full metadata"                     — what is IN;
 *   - "Keeps <props> out of the diff"      — what is OUT;
 *   - "so volatile storage drift doesn't bury real differences" — WHY;
 *   - `nothingPreIgnoredPhrase(...)` + "so every property counts as a
 *     difference" — the empty-list (SQLite) branch's own IN/OUT pair, whose
 *     P-2 wording is pinned by `SchemaCompareSetupDialog.defaultDepth.test.tsx`
 *     and shared with the tooltip and the deep-unavailable note.
 */
function depthDescriptions(
  engine: DatabaseEngineId | undefined,
  engineName: string,
): Record<Exclude<CompareDepth, 'custom'>, string> {
  const preIgnored = getStandardIgnoredProperties(engine);
  const fullFetch = 'Full metadata — everything Quick skips.';

  if (preIgnored.length === 0) {
    return {
      quick: QUICK_DESCRIPTION,
      standard: `${fullFetch} ${nothingPreIgnoredPhrase(engineName)}, so every property counts as a difference.`,
      // W5.2 fix round 2 (P-1): the CARD text is dimmed by
      // `disabled:opacity-50`, so it must not be the only place the reason
      // lives. This line is the short redundant label; the authoritative
      // explanation renders below the radiogroup at full contrast.
      deep: `Same comparison as Standard on ${engineName}.`,
    };
  }

  // Two engines now have a single-item list (mysql `autoIncrement`, pgsql
  // `fillFactor`), so the verb has to agree — "fillFactor count as differences"
  // was a grammar bug waiting for round 3 to expose it.
  const list = preIgnored.join(', ');
  const counts =
    preIgnored.length === 1 ? 'counts as a difference too' : 'count as differences too';
  return {
    quick: QUICK_DESCRIPTION,
    standard: `${fullFetch} Keeps ${list} out of the diff, so volatile storage drift doesn't bury real differences.`,
    deep: `Same fetch as Standard, nothing pre-ignored — ${list} ${counts}.`,
  };
}

/**
 * The one short line each card carries, with the full description moved to the
 * card's own hover.
 *
 * R4 (showcase review round 4) — three paragraphs of body text, side by side,
 * for a control most users set once. The subtitle answers "which one do I
 * want?" at a glance; the paragraph above answers "what exactly does it do?"
 * and is one hover away, unchanged. Deep's line names the properties rather
 * than saying "nothing pre-ignored", because the difference between Standard
 * and Deep IS that list — and on an engine where the list is empty there is no
 * difference to name, so it says so.
 */
function depthSubtitles(
  engine: DatabaseEngineId | undefined,
  engineName: string,
): Record<Exclude<CompareDepth, 'custom'>, string> {
  const preIgnored = getStandardIgnoredProperties(engine);
  return {
    quick: 'Columns only',
    standard: 'Full metadata',
    deep:
      preIgnored.length === 0
        ? `Same as Standard on ${engineName}`
        : `Also diffs ${preIgnored.join(', ')}`,
  };
}

/** Id of the full-contrast note below the depth cards (P-1). */
const DEEP_UNAVAILABLE_NOTE_ID = 'schema-compare-depth-deep-unavailable';

/** Id of a card's full description — the accessible half of its tooltip. */
const depthDescriptionId = (preset: Exclude<CompareDepth, 'custom'>) =>
  `schema-compare-depth-${preset}-description`;

export function SchemaCompareInlineOptions({
  options,
  onChange,
  engine,
  destinationEngine,
  schemaFilter,
  onSchemaFilterChange,
  disabled = false,
  singleObjectMode = false,
}: Props) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const depth = useMemo(() => inferDepth(options, engine), [options, engine]);
  const { track } = useAnalytics();
  const setSchemaCompareDefaultOptions = useAppStore((s) => s.setSchemaCompareDefaultOptions);

  const engineName = useMemo(
    () => (engine && getEngineDisplayInfo(engine)?.displayName) || 'this engine',
    [engine],
  );
  const descriptions = useMemo(
    () => depthDescriptions(engine, engineName),
    [engine, engineName],
  );
  const subtitles = useMemo(
    () => depthSubtitles(engine, engineName),
    [engine, engineName],
  );
  // W5.2 fix round 1 (F-1): Deep is unreachable on an engine Jam pre-ignores
  // nothing for — picking it writes the identical options object Standard
  // writes, so `inferDepth` would snap the highlight straight back to Standard
  // and the click would read as ignored. Disable it there and say why, rather
  // than shipping a control that silently no-ops. `engine === undefined` keeps
  // all three enabled: that's a transient pre-engine-resolution state, not an
  // engine without a distinct Deep.
  //
  // Round 2 (P-1): the "why" lives in a note BELOW the radiogroup, not inside
  // the button — `disabled:opacity-50` dims the button's own body text, which
  // would put the one sentence that justifies disabling the control under the
  // WCAG AA contrast floor. `aria-describedby` ties the two together.
  const deepUnavailable = !!engine && !deepDiffersFromStandard(engine);

  // F02 (SoporteFixes4 W5.3) — the live "Skipping: …" summary and the
  // "Advanced… (N skipped)" button label both read this SAME array: whatever
  // Standard pre-ignored plus every property the user has since unticked in
  // Advanced → "Properties to compare" (`options` here is `effectiveOptions`,
  // so it's populated even before the user has touched anything — the
  // Standard-depth fallback already resolved it). No second derivation, no
  // hardcoded property names — this IS `getStandardIgnoredProperties(engine)`
  // plus the user's own unticks, because that's exactly how this array is
  // built (`applyDepthPreset` seeds it, `IgnoredPropertiesSection.handleToggle`
  // grows it).
  //
  // Fix round 1 (Low #2): `options.ignoredProperties` is computed against
  // `getStandardIgnoredProperties(engine)`, whose `engine === undefined`
  // branch falls back to a 4-item cross-engine union (`fillFactor`,
  // `autoIncrement`, `pctFree`, `iniTrans`) that is wrong for every one of the
  // 5 real engines. That fallback is fine as *input to a depth preset*
  // (nothing user-visible reads it directly pre-resolution); it is NOT fine
  // rendered here as a factual "this is what's being skipped" claim, unlike
  // the depth-card sentence below, which is explicit preset education, not a
  // per-comparison status readout. Suppress both surfaces during the
  // transient pre-engine-resolution window instead of asserting a wrong list.
  const ignoredProperties = engine ? (options.ignoredProperties ?? []) : [];
  const ignoredCount = ignoredProperties.length;

  // R3-5b — the disclosure rule for this row, stated once:
  //
  //   **Everything this comparison will leave out is named here, and a DEEPER
  //   skip is never disclosed more quietly than a shallower one.**
  //
  // Before this, the row showed `Skipping: fillFactor, dataCompression` under
  // Standard and NOTHING under Quick — even though Quick omits five whole
  // property classes it never fetches at all. A reader scanning the row (rather
  // than re-reading the highlighted depth card above it) would conclude Quick
  // skips less than Standard. Backwards, on the one surface this wave exists to
  // make honest.
  //
  // The two skips are disclosed SEPARATELY rather than merged into one list,
  // because their remedies differ and `compareDepth.ts`'s
  // `QUICK_DEPTH_SKIPPED_CLASSES` header spells out why merging them would be
  // wrong: an `ignoredProperties` entry was fetched and can be un-ticked in
  // Advanced → "Properties to compare" (so its chip is a BUTTON that opens
  // exactly that), whereas nothing in the Quick list was ever read and no
  // checkbox can bring it back — the only remedy is the depth radio directly
  // above. That line is therefore plain text, not a button: a control whose
  // click opened Advanced would point at the wrong remedy.
  //
  // Pre-run and general-case by construction (`quickDepthSkippedList()`'s
  // default argument), which is what this surface needs — no run has happened
  // yet. A surface describing a run that HAS happened renders the fetch's own
  // recorded classes instead (the results-side `table-detail-not-compared`
  // banner), which is why nothing was added to the results toolbar here: that
  // banner already says it, louder, with the run's real classes.
  const skipsTableDetail = options.detailed === false;

  // Fix round 1 (Minor #1): both call sites used to report `surface: 'setup'`
  // indistinguishably, so "did the new summary affordance get used" (the
  // question this wave's analytics exists to answer) couldn't be told apart
  // from ordinary Advanced-button clicks. `surface` now names which control
  // was actually clicked.
  const handleOpenAdvanced = useCallback(
    (surface: 'setup-summary' | 'setup-advanced') => {
      if (ignoredCount > 0) {
        track('feature:schema_compare_ignored_summary_open', {
          count: ignoredCount,
          surface,
        });
      }
      setAdvancedOpen(true);
    },
    [ignoredCount, track],
  );

  // W5.1 (D4) — "Save as my defaults": snapshots the fully-materialized
  // current options as the baseline every future fresh compare against this
  // engine starts from (`SchemaCompareSetupDialog`'s auto-seed effect reads
  // it back via `getSchemaCompareDefaultOptions`).
  const handleSaveAsDefaults = useCallback(() => {
    if (!engine) return;
    const materialized = applyEngineDefaults(options);
    setSchemaCompareDefaultOptions(engine, materialized);
    track('feature:schema_compare_save_default_options', { engine });
    const engineName = getEngineDisplayInfo(engine)?.displayName ?? 'this engine';
    toast.success({
      title: 'Saved as your default options',
      description: `New ${engineName} comparisons will start with these settings.`,
    });
  }, [engine, options, setSchemaCompareDefaultOptions, track]);

  // W5.1b — "Load options…" applies to THIS dialog session only: writes into
  // the raw `options`/`schemaFilter` state, never into the saved per-engine
  // defaults (that's `handleSaveAsDefaults` above, a deliberately separate
  // action the user has to click on purpose).
  const handleLoadOptionsFile = useCallback(
    (loaded: SchemaCompareOptions, loadedFilter: SchemaFilter | undefined) => {
      onChange(loaded);
      onSchemaFilterChange(loadedFilter);
    },
    [onChange, onSchemaFilterChange],
  );

  const objectTypes = engine
    ? ENGINE_OBJECT_TYPES[engine]
    : {
        tables: true,
        views: true,
        procedures: true,
        functions: true,
        sequences: false,
        userTypes: false,
      };

  // The ONLY write that may put `detailed` / `ignoredProperties` into the
  // dialog's raw state: the user explicitly picked a depth (W5.2 fix round 1,
  // F-2). Applied to `prev` (raw) rather than the displayed `options`, since
  // the preset overwrites both depth keys outright anyway.
  const handleDepth = useCallback(
    (next: Exclude<CompareDepth, 'custom'>) => {
      onChange((prev) => applyDepthPreset(next, prev, engine));
    },
    [onChange, engine],
  );

  // F-2: object-type checkboxes have nothing to do with depth, so they must
  // merge into the RAW options. Spreading the displayed `options` prop here is
  // what let the Standard-depth fallback's derived `detailed`/`ignoredProperties`
  // be committed to state by an unrelated click — after which a later
  // engine switch's saved-default merge (`{ ...saved, ...prev }`) let the
  // poisoned `prev` win and silently dropped the saved default's depth.
  const handleField = useCallback(
    (key: keyof SchemaCompareOptions, value: boolean) => {
      onChange((prev) => ({ ...prev, [key]: value }));
    },
    [onChange],
  );

  // Fires on both ON and OFF with `{ enabled }` — a toggle, per the analytics
  // convention. No schema, database or sequence name goes near this: the flag
  // is the whole payload.
  const handleSequencesToggle = useCallback(
    (next: boolean) => {
      onChange({ ...options, includeSequences: next });
      track('feature:schema_compare_sequences_toggle', { enabled: next });
    },
    [onChange, options, track],
  );

  // S7 — same convention as the sequences toggle above: both directions,
  // `{ enabled }` is the whole payload.
  const handleUserTypesToggle = useCallback(
    (next: boolean) => {
      onChange({ ...options, includeUserTypes: next });
      track('feature:schema_compare_user_types_toggle', { enabled: next });
    },
    [onChange, options, track],
  );

  // R3-2 — "Wrap in a single transaction", moved here from the results toolbar.
  //
  // ONE input decides both whether the control is rendered and what "unset"
  // means: `engineWrapsInTransaction(destinationEngine)`. The toolbar version
  // took a separate `showWrapInTransaction` boolean plus an `engine` for the
  // tooltip, two props that could disagree about which engines offer the box —
  // and a box hidden on an engine that nevertheless defaults to ON hands that
  // user a wrapped script with no way to see or undo the choice. Deriving both
  // from the single `WRAP_CAPABLE_ENGINES` list makes that unrepresentable.
  //
  // Hidden rather than shown-disabled on the other engines, for the reason
  // `wrapCapableEngines.ts` records: MySQL and Oracle commit implicitly around
  // every DDL statement and SQLite's rollback is unverified for this script
  // shape, so a visible control would advertise a capability they lack.
  const showWrapInTransaction = engineWrapsInTransaction(destinationEngine);
  // `?? showWrapInTransaction`, not `?? true`: the same expression that decides
  // whether the box exists decides what an absent value means, so the two can
  // never be made to disagree by a later edit.
  const wrapInTransaction = options.wrapScriptInTransaction ?? showWrapInTransaction;
  const handleWrapInTransactionToggle = useCallback(
    (next: boolean) => {
      // Not a depth choice — merges into the RAW options via the updater form,
      // like `handleField` and `handleFormatSqlToggle` (F-2).
      onChange((prev) => ({ ...prev, wrapScriptInTransaction: next }));
      // Same event name and payload the results toolbar's checkbox fired before
      // the move: this control changed address, not identity, and splitting the
      // history in two would throw away the one question a default-ON change
      // raises — whether PostgreSQL users opt back out. Low-cardinality engine
      // id only.
      track('feature:schema_compare_wrap_transaction', {
        enabled: next,
        // Always defined in practice — the control only renders behind
        // `engineWrapsInTransaction(destinationEngine)`, which answers false
        // for `undefined`. The fallback is there because event properties are
        // scalars, and `'unknown'` turning up in the dashboard would be a
        // legible bug report rather than a silently dropped event.
        engine: destinationEngine ?? 'unknown',
      });
    },
    [onChange, track, destinationEngine],
  );

  // S2 (1.4.22, Soporte 8 F1) — "Ignore column collation", the option the
  // reporter reached for in Visual Studio's Schema Compare. NOT a new DTO
  // field: it is a first-class view over the existing `ignoredProperties`
  // channel — the same state the depth presets, the Advanced "Properties to
  // compare" checklist, the per-diff-row EyeOff affordance (W5.5) and the
  // "Skipping: …" summary all read and write — so there is exactly one source
  // of truth for what the diff suppresses. Offered only on engines whose
  // comparers can emit the property at all (`ENGINE_PROPERTY_ALLOWLIST`,
  // the same gate the per-row affordance uses).
  //
  // The write is a DELIBERATE `ignoredProperties` write (like `handleDepth`),
  // computed from the DISPLAYED list — options here carry the Standard-depth
  // fallback, so basing the new array on `prev`'s raw (possibly undefined)
  // list would silently drop the preset's own pre-ignores (fillFactor,
  // dataCompression) the moment this box was ticked. Ticking it on a preset
  // depth reads back as 'custom' via `inferDepth`, exactly like the per-row
  // ignore does.
  const engineHasCollationProperty =
    !!engine && (ENGINE_PROPERTY_ALLOWLIST[engine] ?? []).includes('collation');
  const collationIgnored = (options.ignoredProperties ?? []).includes('collation');
  const handleIgnoreCollationToggle = useCallback(
    (next: boolean) => {
      const effective = options.ignoredProperties ?? [];
      const nextIgnored = next
        ? Array.from(new Set([...effective, 'collation']))
        : effective.filter((p) => p !== 'collation');
      onChange((prev) => ({ ...prev, ignoredProperties: nextIgnored }));
      // Toggle convention: fires on both directions, `{ enabled }` is the
      // whole payload — no schema/database/column name goes near this.
      track('feature:schema_compare_ignore_collation_toggle', { enabled: next });
    },
    [onChange, options.ignoredProperties, track],
  );

  // Soporte 9 F1 — "Ignore object owner", the Apex SQL Diff option the
  // reporter asked for by name. Unlike the collation toggle above this IS its
  // own DTO field (`ignoreOwner`): it suppresses header-qualifier drift in
  // MODULE DEFINITIONS, not a column property, so `ignoredProperties` (a
  // column-property channel read by `detectColumnChanges`) is the wrong home.
  // Engine-gated through the SHARED capability module — the comparer, the
  // results banner, the options-file import and this checkbox must all give
  // the same answer, or the option can become active on an engine with no
  // control to clear it (see `ownerIgnoreCapableEngines.ts`).
  const ownerIgnoreVisible = engineSupportsIgnoreOwner(engine);
  const ownerIgnored = options.ignoreOwner === true;
  const handleIgnoreOwnerToggle = useCallback(
    (next: boolean) => {
      onChange((prev) => ({ ...prev, ignoreOwner: next }));
      track('feature:schema_compare_ignore_owner_toggle', { enabled: next });
    },
    [onChange, track],
  );

  // Default true (matches applyEngineDefaults). Treat undefined as on so the
  // checkbox reflects the actual classification behavior before the user
  // ever toggles it.
  const formatSqlEnabled = options.formatSqlForComparison ?? true;
  const handleFormatSqlToggle = useCallback(
    (next: boolean) => {
      // F-2 — same reasoning as `handleField`: not a depth choice, so it
      // merges into the raw options.
      onChange((prev) => ({ ...prev, formatSqlForComparison: next }));
      track('feature:schema_compare_format_sql_toggle', { enabled: next });
    },
    [onChange, track],
  );

  return (
    <div className="space-y-3 border rounded-lg p-4" data-testid="schema-compare-inline-options">
      {/* Compare depth */}
      <TooltipProvider delayDuration={200}>
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Compare depth
            </Label>
            {depth === 'custom' && (
              <span
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                data-testid="schema-compare-depth-custom-badge"
              >
                Custom
              </span>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="What does compare depth control?"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs leading-snug">
                {deepUnavailable ? (
                  <>
                    Quick fetches columns only, for speed on very large
                    schemas. Standard fetches the full per-table metadata.
                    Deep differs from Standard only by not pre-ignoring
                    volatile storage properties, and{' '}
                    {nothingPreIgnoredPhrase(engineName)} — so the two are the
                    same comparison here and Standard is as deep as it gets.
                  </>
                ) : (
                  <>
                    Quick fetches columns only, for speed on very large
                    schemas. Standard and Deep fetch exactly the same full
                    metadata — the only difference is that Standard keeps{' '}
                    {getStandardIgnoredProperties(engine).join(', ')} out of
                    the diff, so routine storage drift doesn't read as a
                    schema change.
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          </div>

          <div
            className="grid grid-cols-1 sm:grid-cols-3 gap-2"
            role="radiogroup"
            aria-label="Compare depth"
          >
            {(['quick', 'standard', 'deep'] as const).map((preset) => {
              const checked = depth === preset;
              const unavailable = preset === 'deep' && deepUnavailable;
              return (
                <Tooltip key={preset}>
                  <TooltipTrigger asChild>
                    {/* The wrapper, not the button, is the hover surface: a
                        disabled control fires no tooltip in Chromium, and the
                        Deep card is exactly the one whose description a user
                        most wants to read while it is unavailable. */}
                    <span className="inline-flex">
                      <button
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        disabled={disabled || unavailable}
                        aria-describedby={
                          unavailable
                            ? `${depthDescriptionId(preset)} ${DEEP_UNAVAILABLE_NOTE_ID}`
                            : depthDescriptionId(preset)
                        }
                        onClick={() => handleDepth(preset)}
                        data-testid={`schema-compare-depth-${preset}`}
                        className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
                          checked
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/50'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        <div className="text-sm font-medium capitalize">{preset}</div>
                        <div
                          className="text-xs text-muted-foreground"
                          data-testid={`schema-compare-depth-${preset}-subtitle`}
                        >
                          {subtitles[preset]}
                        </div>
                      </button>
                      {/* The full description as REAL text, outside the card so
                          it doesn't lengthen it, and referenced by
                          `aria-describedby`: a tooltip is not an accessible
                          description, and hover does not exist on touch. Sighted
                          mouse users get the same sentence from the tooltip. */}
                      <span
                        id={depthDescriptionId(preset)}
                        className="sr-only"
                        data-testid={`schema-compare-depth-${preset}-description`}
                      >
                        {descriptions[preset]}
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs leading-snug">
                    {descriptions[preset]}
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </div>

          {/* W5.2 fix round 2 (P-1) — the reason the Deep card is disabled,
              rendered OUTSIDE the dimmed button so it keeps full contrast for
              touch, keyboard and screen-reader users (the card itself is
              greyed by `disabled:opacity-50`, which would drag
              `text-muted-foreground` under WCAG AA). Not a tooltip: a disabled
              button doesn't reliably fire one, and hover isn't available on
              touch. `text-foreground` rather than muted for the same reason. */}
          {deepUnavailable && (
            <p
              id={DEEP_UNAVAILABLE_NOTE_ID}
              data-testid="schema-compare-depth-deep-unavailable"
              className="mt-2 text-xs text-foreground"
            >
              <span className="font-medium">Deep is unavailable on {engineName}.</span>{' '}
              {nothingPreIgnoredPhrase(engineName)}, so Deep would run exactly the
              same comparison as Standard.
            </p>
          )}
        </div>
      </TooltipProvider>

      {/* R4-2 — the two script/comparison toggles share ONE row, wrap first.
          They were stacked, which read as a list of unrelated flags and cost a
          whole row of dialog height on short screens. Wrap sits to the LEFT
          because it is the one with a destructive-side consequence (an
          all-or-nothing script), so it should be the first thing read, and
          because it now carries a glyph the eye can find.

          `flex-wrap` on the row, not a fixed two-up grid: at narrow widths the
          second toggle drops to its own line rather than squeezing the labels
          — the R3-1 lesson (a `truncate` child in an unwrapped flex row eats
          all the overflow) applied before it can happen again. */}
      <TooltipProvider delayDuration={200}>
        <div className="flex items-center gap-x-5 gap-y-1.5 pt-1 flex-wrap">
        {/* R3-2 — "Wrap in a single transaction", the one option in this block
            that describes the generated SCRIPT rather than the comparison.

            It was a bare checkbox on the results toolbar, where it competed with
            Compare and Generate Script for horizontal space (and contributed to
            the R3-1 overflow), and where it could be neither shared nor saved: a
            per-tab flag nobody else on the team could inherit. Here it exports
            with the options file, loads with it, and is captured by "Save as my
            defaults" — which is what a team that has decided "our sync scripts
            are all-or-nothing" actually needs.

            The engine-aware tooltip travels unchanged and is still a plain
            `title` on the label (`wrapInTransactionTooltip`): SQL Server reads
            `SET XACT_ABORT ON + BEGIN TRANSACTION` and discloses the syntax-error
            hole, PostgreSQL reads `BEGIN; … COMMIT;` and states there is no such
            exception. The two strings are pinned byte-for-byte in
            `utils/wrapInTransactionTooltip.test.ts`; making one engine right by
            rewording the other is the same defect with the engines swapped.

            `ShieldCheck` is NOT a new glyph choice — it is already this app's
            transaction mark, on the Query Editor's isolation-level control
            (`TransactionToolbarControls.tsx`). A second, different transaction
            icon would teach two symbols for one concept.

            `data-testid`s are deliberately the SAME as the toolbar's were — the
            control moved, it was not replaced, and the docs captures and e2e
            specs that point at it should keep pointing at it. */}
        {showWrapInTransaction && (
          <div
            className="flex items-center gap-2"
            data-testid="schema-compare-wrap-transaction"
          >
            <Checkbox
              id="schema-compare-wrap-transaction-checkbox"
              checked={wrapInTransaction}
              onCheckedChange={(v) => handleWrapInTransactionToggle(v === true)}
              disabled={disabled}
              data-testid="schema-compare-wrap-transaction-checkbox"
            />
            <Label
              htmlFor="schema-compare-wrap-transaction-checkbox"
              className="text-sm cursor-pointer flex items-center gap-1.5"
              title={wrapInTransactionTooltip(destinationEngine)}
            >
              <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              Wrap in a single transaction
            </Label>
          </div>
        )}

        {/* Format SQL during comparison — first-class toggle.
            When ON, view/routine/trigger DDL is formatted before classification
            so two routines that differ only in indentation/case collapse to
            identical. The per-pane Formatted/Raw pill in the diff viewer lets
            users see Raw for one comparison without re-running. */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="option-format-sql-for-comparison"
            checked={formatSqlEnabled}
            onCheckedChange={(v) => handleFormatSqlToggle(v === true)}
            disabled={disabled}
            data-testid="option-format-sql-for-comparison"
          />
          <Label
            htmlFor="option-format-sql-for-comparison"
            className="text-sm cursor-pointer flex items-center gap-1.5"
          >
            <Wand2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            Format SQL during comparison
          </Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label="What does Format SQL during comparison do?"
                className="text-muted-foreground hover:text-foreground"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs leading-snug">
              Runs view, procedure, function and trigger definitions through
              the engine-aware SQL formatter before comparing. Routines that
              differ only in indentation or keyword case collapse to
              "Identical". The DDL diff pane has a Formatted/Raw toggle if
              you want to see the original text.
            </TooltipContent>
          </Tooltip>
        </div>

        {/* S2 — Ignore column collation (VS Schema Compare parity). See the
            handler's comment for why this is a view over `ignoredProperties`
            rather than its own DTO field, and why it's allowlist-gated. */}
        {engineHasCollationProperty && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="option-ignore-column-collation"
              checked={collationIgnored}
              onCheckedChange={(v) => handleIgnoreCollationToggle(v === true)}
              disabled={disabled}
              data-testid="option-ignore-column-collation"
            />
            <Label
              htmlFor="option-ignore-column-collation"
              className="text-sm cursor-pointer flex items-center gap-1.5"
            >
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              Ignore column collation
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="What does Ignore column collation do?"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs leading-snug">
                Column collation differences are left out of the results, and
                the generated script never changes a column's collation — new
                tables and columns inherit the target database's default.
                Useful when the two databases deliberately run different
                collations. Same effect as adding "collation" to the ignored
                properties.
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        {/* Soporte 9 F1 — Ignore object owner (Apex SQL Diff parity). See the
            handler's comment for why this is its own DTO field and why it's
            MSSQL-gated. */}
        {ownerIgnoreVisible && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="option-ignore-object-owner"
              checked={ownerIgnored}
              onCheckedChange={(v) => handleIgnoreOwnerToggle(v === true)}
              disabled={disabled}
              data-testid="option-ignore-object-owner"
            />
            <Label
              htmlFor="option-ignore-object-owner"
              className="text-sm cursor-pointer flex items-center gap-1.5"
            >
              <EyeOff className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              Ignore object owner
            </Label>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="What does Ignore object owner do?"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs leading-snug">
                The schema qualifier in a view, procedure, function or trigger
                definition's CREATE header stops counting as a difference — SQL
                Server stores that text exactly as typed, so CREATE PROCEDURE
                [dbo].[name] on one side and CREATE PROCEDURE [name] on the
                other otherwise report the same object as modified. A qualifier
                inside the body still counts.
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        </div>
      </TooltipProvider>

      {/* Object types + Advanced trigger.
          W3.5/F07 seam 2: in single-object mode the object-type choice is
          not merely disabled, it is meaningless — the object picker already
          picked the one type this compare will ever touch. Hide the row
          entirely rather than render a checkable-but-inert control. */}
      <div
        className={`flex items-start gap-3 pt-1 flex-wrap ${
          singleObjectMode ? 'justify-end' : 'justify-between'
        }`}
      >
        {!singleObjectMode && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
            <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Object types
            </Label>
            {objectTypes.tables && (
              <ObjectTypeCheckbox
                id="option-include-tables"
                label="Tables"
                checked={options.includeTables ?? true}
                onChange={(v) => handleField('includeTables', v)}
                disabled={disabled}
              />
            )}
            {objectTypes.views && (
              <ObjectTypeCheckbox
                id="option-include-views"
                label="Views"
                checked={options.includeViews ?? true}
                onChange={(v) => handleField('includeViews', v)}
                disabled={disabled}
              />
            )}
            {objectTypes.procedures && (
              <ObjectTypeCheckbox
                id="option-include-procedures"
                label="Procedures"
                checked={options.includeProcedures ?? true}
                onChange={(v) => handleField('includeProcedures', v)}
                disabled={disabled}
              />
            )}
            {objectTypes.functions && (
              <ObjectTypeCheckbox
                id="option-include-functions"
                label="Functions"
                checked={options.includeFunctions ?? true}
                onChange={(v) => handleField('includeFunctions', v)}
                disabled={disabled}
              />
            )}
            {/* Shown only for engines that really enumerate sequences
                (SQL Server / PostgreSQL / Oracle) — see ENGINE_OBJECT_TYPES. */}
            {objectTypes.sequences && (
              <ObjectTypeCheckbox
                id="option-include-sequences"
                label="Sequences"
                checked={options.includeSequences ?? true}
                onChange={handleSequencesToggle}
                disabled={disabled}
              />
            )}
            {/* S7 — SQL Server only: table types, alias types, CLR types. */}
            {objectTypes.userTypes && (
              <ObjectTypeCheckbox
                id="option-include-user-types"
                label="User-defined types"
                checked={options.includeUserTypes ?? true}
                onChange={handleUserTypesToggle}
                disabled={disabled}
              />
            )}
          </div>
        )}

        {/* R3-5 — the button row.
            `flex-wrap` + `min-w-0`: this was an unwrapped `flex items-center
            gap-2` whose only shrinkable child was the "Skipping: …" label
            (`truncate` gives a flex item an automatic minimum size of 0, which
            a `max-w-[320px]` cap cannot stop). So the label absorbed the entire
            width deficit while `Advanced…` still overflowed the card edge and
            forced a horizontal scrollbar. Wrapping the buttons removes the
            deficit at source; `justify-end` keeps them right-aligned on every
            line they wrap onto. The summary itself has moved off this row
            entirely (below) so it is no longer competing with them at all. */}
        <div className="flex flex-wrap items-center justify-end gap-2 min-w-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5"
            onClick={handleSaveAsDefaults}
            disabled={disabled || !engine}
            data-testid="schema-compare-save-defaults-button"
            title="Save depth, object types, and ignored properties as your default for this engine — every new compare starts here."
          >
            <Save className="h-3.5 w-3.5" />
            Save as my defaults
          </Button>
          <SchemaCompareOptionsFileButtons
            options={options}
            schemaFilter={schemaFilter}
            engine={engine}
            onLoad={handleLoadOptionsFile}
            disabled={disabled}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1.5"
            onClick={() => handleOpenAdvanced('setup-advanced')}
            disabled={disabled}
            data-testid="schema-compare-advanced-button"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {ignoredCount > 0 ? `Advanced… (${ignoredCount} skipped)` : 'Advanced…'}
          </Button>
        </div>
      </div>

      {/* R3-5 / R3-5b — "what this comparison will leave out", on its OWN row.
          Off the button line (so it can no longer be the row's shrink victim)
          and gets the full card width, which is what the `truncate` is sized
          against now. Two entries, never merged — see `skipsTableDetail`'s
          comment above for why the remedies differ and why only one of them is
          clickable. */}
      {(skipsTableDetail || ignoredCount > 0) && (
        <div
          className="flex flex-wrap items-baseline gap-x-4 gap-y-1 min-w-0"
          data-testid="schema-compare-setup-skips-row"
        >
          {skipsTableDetail && (
            <span
              className="text-xs text-muted-foreground min-w-0 truncate"
              title={`Not fetched at Quick depth: ${quickDepthSkippedList()} — pick Standard or Deep above to compare them`}
              data-testid="schema-compare-setup-quick-skips"
            >
              Not fetched at Quick depth: {quickDepthSkippedList()}
            </span>
          )}
          <IgnoredPropertiesSummaryButton
            ignoredProperties={ignoredProperties}
            onClick={() => handleOpenAdvanced('setup-summary')}
            testId="schema-compare-setup-ignored-summary"
          />
        </div>
      )}

      <SchemaCompareAdvancedDialog
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        options={options}
        onChange={onChange}
        engine={engine}
        disabled={disabled}
      />
    </div>
  );
}

type ObjectTypeCheckboxProps = {
  id: string;
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled: boolean;
};

function ObjectTypeCheckbox({
  id,
  label,
  checked,
  onChange,
  disabled,
}: ObjectTypeCheckboxProps) {
  return (
    <div className="flex items-center gap-1.5">
      <Checkbox
        id={id}
        checked={checked}
        onCheckedChange={(v) => onChange(v === true)}
        disabled={disabled}
        data-testid={id}
      />
      <Label htmlFor={id} className="text-sm cursor-pointer">
        {label}
      </Label>
    </div>
  );
}
